<template>
	<section class="scene-trainer-game">
		<ChalkyTrainerSceneGame
			ref="table"
			v-bind:debugMode="false"
			v-bind:showEnglishMeter="true"
			v-bind:showFpsCounter="false"
			v-bind:showPowerMeter="true"
			v-on:game:ready="Handle_OnGameReady"
		/>

		<section id="demo-flag">
			<span class="badge background-chalky-red">DEMO</span>
		</section>

		<section id="overlay">
			<section id="menu" class="padded">
				<!-- <button class="control round type-menu">Menu</button> -->

				<nav>
					<button v-on:click="Handle_OnClickRackEight">
						<img
							class="filter-invert"
							height="24"
							src="~@chalkysticks/sass/build/asset/image/tv/channel-8ball.svg"
							alt="8-Ball"
						/>
					</button>

					<button v-on:click="Handle_OnClickRackTen">
						<img
							class="filter-invert"
							height="24"
							src="~@chalkysticks/sass/build/asset/image/tv/channel-10ball.svg"
							alt="8-Ball"
						/>
					</button>

					<button v-on:click="Handle_OnClickRackNine">
						<img
							class="filter-invert"
							height="24"
							src="~@chalkysticks/sass/build/asset/image/tv/channel-9ball.svg"
							alt="8-Ball"
						/>
					</button>

					<button v-on:click="Handle_OnClickShuffle">
						<img
							class="filter-invert"
							height="24"
							src="~@chalkysticks/sass/build/asset/image/icon/shuffle.svg"
							alt="Shuffle"
						/>
					</button>

					<button v-on:click="Handle_OnClickEnvironment">
						<img
							class="filter-invert"
							height="24"
							src="~@chalkysticks/sass/build/asset/image/icon/home.svg"
							alt="Shuffle"
						/>
					</button>

					<button v-on:click="Handle_OnClickTable">
						<svg width="35" height="24" viewBox="0 0 35 25" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M34.8802 0V6.81567H0V0H34.8802ZM1.20276 5.6129H33.6774V1.20276H1.20276V5.6129Z"
								fill="#ffffff"
							/>
							<path
								d="M30.8708 24.4562L24.0552 24.4562L24.0552 5.61291L30.8708 5.61292L30.8708 24.4562ZM25.2579 6.81568L25.2579 23.2535L29.6681 23.2535L29.6681 6.81568L25.2579 6.81568Z"
								fill="#ffffff"
							/>
							<path
								d="M10.825 24.4562L4.00929 24.4562L4.00929 5.61291L10.825 5.61292L10.825 24.4562ZM5.21205 6.81568L5.21205 23.2535L9.62219 23.2535L9.62219 6.81568L5.21205 6.81568Z"
								fill="#ffffff"
							/>
						</svg>
					</button>
				</nav>
			</section>
		</section>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Trainer game scene with 3D pool table, ball racking controls, and
	 * environment customization
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Trainer
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneTrainerGame extends SceneBase {
		/**
		 * @type ChalkySticks.Model.Diagram | null
		 */
		@Prop()
		public diagramModel!: ChalkySticks.Model.Diagram;

		/**
		 * @return Promise<void>
		 */
		protected loadBalls(): any {
			const tableState = this.$store.state.pad.activeTableState;
			const customRack: any = [];
			let layout: any = this.diagramModel.getLayout();

			// If we're storing a custom state, use that
			if (tableState?.layout) {
				layout = tableState.layout;
			}

			// If we dont have any balls, bail
			if (!layout?.balls) {
				return [];
			}

			// Format the balls in a way Trainer will understand
			layout.balls.forEach((layoutBall: any) => {
				const ball = this.getBallType(layoutBall.type);
				const x = layoutBall.normalizedX;
				const y = layoutBall.normalizedY;

				if (ball < 0) {
					return;
				}

				customRack.push({
					ball,
					x,
					y,
				});
			});

			console.log('customRack', customRack);

			return customRack;
		}

		/**
		 * @param string
		 * @return number
		 */
		protected getBallType(ballType: string): number {
			const number = ballType.match(/\d+/);

			if (ballType.includes('cue')) {
				return 0;
			}

			return number ? parseInt(number[0], 10) : -1;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickEnvironment(e: PointerEvent): Promise<void> {
			e.preventDefault();

			// Deselect the button after click
			(e.currentTarget as HTMLElement).blur();

			ChalkySticks.Core.Event.Bus.dispatch('environment:preset:cycle');
		}

		/**
		 * Has an issue where it can't load twice
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnGameReady(): Promise<void> {
			const customRack = this.loadBalls() ?? [];

			ChalkySticks.Core.Event.Bus.dispatch('game.control:rackCustom', customRack);
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickRackEight(e: PointerEvent): Promise<void> {
			e.preventDefault();

			ChalkySticks.Core.Event.Bus.dispatch('game.control:rack', 'EightBall');
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickRackNine(e: PointerEvent): Promise<void> {
			e.preventDefault();

			ChalkySticks.Core.Event.Bus.dispatch('game.control:rack', 'NineBall');
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickRackTen(e: PointerEvent): Promise<void> {
			e.preventDefault();

			ChalkySticks.Core.Event.Bus.dispatch('game.control:rack', 'TenBall');
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickShuffle(e: PointerEvent): Promise<void> {
			e.preventDefault();

			ChalkySticks.Core.Event.Bus.dispatch('game.control:shuffle');
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickTable(e: PointerEvent): Promise<void> {
			e.preventDefault();

			// Deselect the button after click
			(e.currentTarget as HTMLElement).blur();

			ChalkySticks.Core.Event.Bus.dispatch('table:appearance:cycle');
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickUndo(e: PointerEvent): Promise<void> {
			e.preventDefault();

			ChalkySticks.Core.Event.Bus.dispatch('game.control:undo');
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-trainer-game {
		#demo-flag {
			// font-size: 11px;
			left: 0;
			padding: 1rem;
			position: absolute;
			top: 0;
			z-index: var(--z-page-top);
		}

		#overlay {
			height: 100%;
			left: 0;
			pointer-events: none;
			position: absolute;
			top: 0;
			width: 100%;
			z-index: var(--z-page-top);

			button {
				align-items: center;
				color: var(--chalky-white);
				display: flex;
				height: 50px;
				justify-content: center;
				pointer-events: auto;
				text-align: center;
				width: 50px;
			}

			#menu {
				height: 100%;
				display: flex;
				flex-direction: column;
				justify-content: flex-end;
				pointer-events: none;
			}
		}

		.scene-game {
			--english-size: 75px;

			.view-english {
				opacity: 0.85;
				transform: scale(0.5);

				&:focus,
				&:active,
				&:hover {
					transform: scale(1);
				}
			}

			.view-power {
				opacity: 0.85;
			}
		}
	}
</style>
